import { ChangeEvent, KeyboardEvent, useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCamera } from '@fortawesome/pro-solid-svg-icons/faCamera';

import {
  namespaces,
  useSharedTranslation,
} from 'us-react-components/dist/hooks/useSharedTranslation';
import { AxiosError, HeicConvertService, SitterService } from 'us-web-services';

import { Button, ButtonType } from '../../components';
import { useDependencies } from '../../dependencies';
import { ModalName } from '../../modals/constants';
import { useModal } from '../../modals/modalContext';

const DEFAULT_AVATAR_FRAGMENT = 'us-default-avatar';
const PHOTO_GUIDELINES_URL =
  'https://urbansitter.zendesk.com/hc/en-us/articles/200337748-iPhoto-guidelines-How-to-select-and-upload-a-great-profile-picture';
const PHONE_SCREENSHOT =
  'https://assets.urbansitter.com/static/img/screenshots/iphone-sitter-profile.webp';

export const SitterRegistrationPhoto = observer((): JSX.Element => {
  const { t } = useSharedTranslation(namespaces.caregiver);
  const navigate = useNavigate();
  const { stores } = useDependencies();
  const modal = useModal();
  const sitter = stores.sitter.sitter;
  const user = stores.user.user;

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    document.title = 'UrbanSitter - Your Profile Photo';
  }, []);

  useEffect(() => {
    if (user && !user.id) {
      navigate('/', { replace: true });
    }
  }, [user, navigate]);

  const hasRealPhoto =
    !!sitter?.pictureUrl && !sitter.pictureUrl.includes(DEFAULT_AVATAR_FRAGMENT);

  // Only read the file buffer when the extension/MIME hints at HEIC. For
  // other image types the browser can decode directly from the object URL,
  // which avoids NotReadableError on iCloud-dehydrated or large files.
  const isProbablyHeic = (file: File): boolean =>
    file.type === 'image/heic' ||
    file.type === 'image/heif' ||
    /\.(heic|heif)$/i.test(file.name);

  const handleUpload = async (file: File): Promise<void> => {
    if (!isProbablyHeic(file)) {
      stores.user.setTemporaryPhoto(URL.createObjectURL(file));
      modal?.setModalName(ModalName.Cropper);
      return;
    }

    let buffer: ArrayBuffer;
    try {
      buffer = await file.arrayBuffer();
    } catch {
      toast.error(t('pages.sitterPhoto.uploadFailed'));
      return;
    }

    if (!HeicConvertService.isHeic(new Uint8Array(buffer))) {
      stores.user.setTemporaryPhoto(URL.createObjectURL(file));
      modal?.setModalName(ModalName.Cropper);
      return;
    }

    const postData = new FormData();
    try {
      postData.append('file', new Blob([buffer]));
      const response = (await HeicConvertService.convert(postData)).data.data;
      const base64 = btoa(
        response.data.reduce(
          (res: string, byte: number) => res + String.fromCharCode(byte),
          '',
        ),
      );
      stores.user.setTemporaryPhoto(`data:image/png;base64,${base64}`);
      modal?.setModalName(ModalName.Cropper);
    } catch (e) {
      if (e instanceof AxiosError && e.response?.data?.error) {
        toast.error(e.response.data.error);
      } else {
        toast.error(t('pages.sitterPhoto.uploadFailed'));
      }
    }
  };

  const onFileChange = (e: ChangeEvent<HTMLInputElement>): void => {
    const file = e.target.files?.[0];
    if (file) {
      handleUpload(file);
    }
  };

  const onFileClick = (): void => {
    // Reset value so selecting the same file re-triggers onChange.
    if (fileInputRef.current?.value) {
      fileInputRef.current.value = '';
    }
  };

  const onLabelKeyDown = (e: KeyboardEvent<HTMLLabelElement>): void => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      fileInputRef.current?.click();
    }
  };

  const goToNext = async (): Promise<void> => {
    if (!sitter?.id || saving) return;
    setSaving(true);
    try {
      await SitterService.patch(sitter.id, {
        id: sitter.id,
        photoReady: true,
      } as never);
      await stores.sitter.refreshSitter?.();
      window.location.assign('/app/sitter/registration/availability');
    } catch (err) {
      console.warn('[SitterPhoto] photoReady patch failed', err);
      setSaving(false);
    }
  };

  return (
    <div className='container py-8'>
      <div className='grid grid-cols-1 sm:grid-cols-12 gap-6'>
        <div className='sm:col-span-5 md:col-span-4 text-center'>
          <div className='w-32 h-32 mx-auto mb-6 relative'>
            {sitter?.pictureUrl && (
              <img
                src={sitter.pictureUrl}
                alt=''
                className={`w-32 h-32 rounded-full object-cover border border-white ${
                  hasRealPhoto
                    ? 'shadow-[0_2px_5px_2px_#ccc]'
                    : 'shadow-[0_2px_5px_2px_theme(colors.sapphire.500)] animate-circle-shadow-pulse'
                }`}
              />
            )}
            {/* Label forwards clicks/Enter to the hidden file input — matches
                the NUX Family photo pattern so the native picker opens reliably
                across browsers (programmatic input.click() on display:none
                inputs is inconsistent). */}
            {/* eslint-disable jsx-a11y/no-noninteractive-tabindex, jsx-a11y/no-noninteractive-element-interactions */}
            <label
              htmlFor='photo'
              tabIndex={0}
              onKeyDown={onLabelKeyDown}
              aria-label={t('pages.sitterPhoto.chooseFile')}
              title={t('pages.sitterPhoto.chooseFile')}
              className={`absolute bottom-0 right-0 w-9 h-9 rounded-full bg-white text-gray-500 flex items-center justify-center cursor-pointer border hover:border-black ${
                hasRealPhoto ? 'border-gray-400' : 'border-error-500'
              }`}
            >
              <FontAwesomeIcon icon={faCamera} />
            </label>
            {/* eslint-enable jsx-a11y/no-noninteractive-tabindex, jsx-a11y/no-noninteractive-element-interactions */}
            <input
              id='photo'
              ref={fileInputRef}
              type='file'
              accept='image/*, .heic'
              className='hidden'
              onChange={onFileChange}
              onClick={onFileClick}
            />
          </div>
          <h1 className='text-xl font-semibold'>{t('pages.sitterPhoto.heading')}</h1>
        </div>

        <div className='sm:col-span-7 md:col-span-8'>
          <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
            <div>
              <img src={PHONE_SCREENSHOT} alt='' className='max-w-full' />
            </div>
            <div className='pt-4 sm:pt-0'>
              <h2 className='text-lg font-semibold mb-2'>
                {t('pages.sitterPhoto.guidelinesHeading')}
              </h2>
              <p>{t('pages.sitterPhoto.guideline1')}</p>
              <p>{t('pages.sitterPhoto.guideline2')}</p>
              <p>{t('pages.sitterPhoto.guideline3')}</p>
              <a
                target='_blank'
                rel='noreferrer'
                href={PHOTO_GUIDELINES_URL}
                className='underline'
              >
                {t('pages.sitterPhoto.faqLink')}
              </a>

              <div className='mt-12 text-center hidden sm:block'>
                <Button
                  id='continue-button'
                  buttonType={ButtonType.primary}
                  disabled={!hasRealPhoto || saving}
                  onClick={goToNext}
                >
                  {t('pages.sitterPhoto.continue')}
                </Button>
                <div className='flex justify-center gap-2 mt-4'>
                  {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
                    <span
                      key={i}
                      className={`w-2 h-2 rounded-full inline-block ${
                        i === 2 ? 'bg-sapphire' : 'border border-neutral-300'
                      }`}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className='block sm:hidden text-center mt-6'>
        <Button
          buttonType={ButtonType.primary}
          disabled={!hasRealPhoto || saving}
          onClick={goToNext}
        >
          {t('pages.sitterPhoto.continue')}
        </Button>
      </div>
    </div>
  );
});
